import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  CheckCircle2, FileSpreadsheet, FileText, Image as ImageIcon, UploadCloud, X
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileUpload = ({
  id,
  file,
  onChange,
  accept = 'image/jpeg,image/png,image/webp',
  hint = 'PNG, JPG, or WebP up to 10 MB',
  className,
}) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return undefined;
    }
    if (typeof file === 'string') {
      setPreview(file);
      return undefined;
    }
    if (file.type?.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreview(null);
    return undefined;
  }, [file]);

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    const droppedFile = event.dataTransfer.files?.[0];
    if (droppedFile) {
      onChange(droppedFile);
    }
  };

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0] || null;
    onChange(selected);
  };

  const clearFile = (event) => {
    event.stopPropagation();
    onChange(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const fileIcon = () => {
    if (file.type === 'application/pdf') return <FileText className="h-5 w-5" />;
    if (/\.(xlsx?|csv)$/i.test(file.name || '') || file.type?.includes('spreadsheet') || file.type?.includes('excel')) {
      return <FileSpreadsheet className="h-5 w-5" />;
    }
    return <ImageIcon className="h-5 w-5" />;
  };

  return (
    <div className={cn('relative', className)}>
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        className="sr-only"
        tabIndex={-1}
      />
      {!file ? (
        <div
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={cn(
            'flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-4 text-center transition-all cursor-pointer select-none',
            dragOver
              ? 'border-primary bg-primary/10 shadow-sm ring-2 ring-primary/20'
              : 'border-slate-200/90 bg-slate-50/50 hover:border-primary/50 hover:bg-primary/5 hover:shadow-sm',
          )}
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white text-primary shadow-sm border border-slate-200/60">
            <UploadCloud className="h-4 w-4" />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-800">
              <span className="text-primary hover:underline">Choose a file</span> or drag and drop
            </p>
            <p className="mt-0.5 text-xs text-slate-400">{hint}</p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 rounded-xl border border-slate-200/90 bg-slate-50/70 p-3 shadow-sm">
          {preview ? (
            <img
              src={preview}
              alt="Selected file preview"
              className="h-12 w-12 shrink-0 rounded-lg border border-slate-200 bg-white object-cover"
            />
          ) : (
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-emerald-600">
              {fileIcon()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="truncate text-xs font-bold text-slate-900">{file.name || 'Attached file'}</span>
              <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-xs font-semibold text-emerald-800">
                <CheckCircle2 className="h-3 w-3" /> Ready
              </span>
            </div>
            {file.size && (
              <p className="mt-0.5 text-xs text-slate-500">{formatFileSize(file.size)}</p>
            )}
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="tm-plain-button mt-1 text-xs font-semibold text-primary hover:underline"
            >
              Change file
            </button>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clearFile}
            className="h-8 w-8 p-0 text-slate-400 hover:bg-red-50 hover:text-red-600"
            title="Remove file"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

FileUpload.propTypes = {
  id: PropTypes.string,
  file: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      name: PropTypes.string,
      type: PropTypes.string,
      size: PropTypes.number,
    }),
  ]),
  onChange: PropTypes.func.isRequired,
  accept: PropTypes.string,
  hint: PropTypes.string,
  className: PropTypes.string,
};

export default FileUpload;
